import { useContext, useMemo } from 'react'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import usePropertyAnytimeRate from 'hooks/Offers/usePropertyAnytimeRate'
import { isStandardOccupancy } from 'lib/search/searchUtils'
import type { AccommodationSearchPresentationInput } from 'lib/search/accommodationSearchPresentation'
import { shouldHideAccommodationSearchPrice } from 'lib/search/accommodationSearchPresentation'

export function useHideAnytimePrices(
  offerType?: App.OfferType,
  metadataHidePricing?: boolean,
  productType?: App.ProductType,
  filters?: App.OfferListFilters,
  accommodationPropertyId?: string,
): boolean {
  const globalSearchState = useContext(GlobalSearchStateContext)
  const hideAnytimePrices = !!globalSearchState?.hideAnytimePrices

  const presentationInput = useMemo<AccommodationSearchPresentationInput>(() => ({
    offerType,
    productType,
    metadataHidePricing,
    hideAnytimePrices,
    hasStandardOccupancy: isStandardOccupancy(filters),
  }), [offerType, productType, metadataHidePricing, hideAnytimePrices, filters])

  // Only look the property up when a dynamic Flash promotion would actually change the outcome —
  // asking the shared policy both ways keeps that in one place, so it can't drift from the rules
  // that hide the price (metadata-hidden pricing, for one, stays hidden either way).
  const isPromotionRelevant = offerType === OFFER_TYPE_ALWAYS_ON &&
    shouldHideAccommodationSearchPrice(presentationInput) &&
    !shouldHideAccommodationSearchPrice({ ...presentationInput, hasDynamicFlashPromotion: true })
  const [anytimeRates] = usePropertyAnytimeRate(accommodationPropertyId, {
    disabled: !isPromotionRelevant,
  })

  return shouldHideAccommodationSearchPrice({
    ...presentationInput,
    hasDynamicFlashPromotion: anytimeRates?.anytimePromotionRate?.promotion.type === 'dynamicFlash',
  })
}

export default useHideAnytimePrices
